import { Directive, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import flattenDeep from 'lodash-es/flattenDeep';
import isNil from 'lodash-es/isNil';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

const CONFIG_KEY = 'im2m-table-config';

type Config = { [key: string]: boolean };

export interface Column {
    /**
     * must be uniq
     */
    key: string;
    label: string;
    visible: boolean;
    /** if column can be toggled */
    toggleable: boolean;
    position: number;
}

export interface ColumnGroup {
    title?: string;
    columns: Column[];
}

@Directive({
    selector: '[liebherrTableConfig]',
    exportAs: 'liebherrTableConfig',
})
export class TableConfigDirective implements OnChanges, OnDestroy {
    @Input() columnGroups: ColumnGroup[] = [];

    /**
     * (optional) id used to identify the table's config saved in localStorage
     * @default `table@<currentPath>`
     * @note when using multiple TableConfigDirective in one view, each one of them needs an individual id
     */
    @Input() id?: string;

    /**
     * BehaviorSubject containing the keys of the currently visible columns of the table
     */
    displayedColumns$ = new BehaviorSubject<string[]>([]);

    private selectedConfig$ = new BehaviorSubject<Config | undefined>(undefined);

    private sub: Subscription = new Subscription();

    private tableId = this.generateTableId();

    private flatColumns: Column[] = [];

    constructor(private router: Router) {
        this.sub.add(
            this.selectedConfig$.pipe(filter((c) => c !== undefined)).subscribe((config) => {
                if (config) {
                    // update config in localStorage
                    this.updateTableConfig(config);
                    // update displayedColumns
                    this.updateDisplayedColumns(config);
                }
            })
        );
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.columnGroups) {
            this.flatColumns = flattenDeep(this.columnGroups.map((g) => g.columns));
            this.getSelectedConfig();
        }
        if (changes.id) {
            this.tableId = this.generateTableId();
            this.getSelectedConfig();
        }
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }

    /**
     * Toggles visibility of mat-table columns
     * @param column key of column; Can be a single string or string[]
     * @param visible (optional) used to set visiblity of columns; When not set the column's current visibility will be inverted
     */
    // tslint:disable-next-line:bool-param-default
    toggleColumn(column: string | string[], visible?: boolean) {
        const config = this.selectedConfig$.getValue();
        if (config) {
            if (Array.isArray(column)) {
                column
                    .filter((col) => !isNil(config[col]))
                    .forEach((col) => {
                        config[col] = !isNil(visible) ? visible : !config[col];
                    });
            } else {
                if (!isNil(config[column])) {
                    config[column] = !isNil(visible) ? visible : !config[column];
                }
            }
        }

        this.selectedConfig$.next(config);
    }
    /**
     * Resets the displayedColumns to the initial value, defined by 'allColumns' and 'hiddenColumns'
     */
    reset() {
        this.selectedConfig$.next(this.createConfig());
    }

    private getSelectedConfig() {
        const tableConfig = localStorage.getItem(CONFIG_KEY);
        let selectedConfig: Config | undefined;
        if (tableConfig) {
            selectedConfig = JSON.parse(tableConfig)[this.tableId];
        }

        if (selectedConfig === undefined || !this.hasCorrectKeys(selectedConfig)) {
            this.selectedConfig$.next(this.createConfig());
        } else {
            this.selectedConfig$.next(selectedConfig);
        }
    }

    private updateTableConfig(config: Config) {
        const tableConfigString: any = localStorage.getItem(CONFIG_KEY);
        let tableConfig = tableConfigString ? JSON.parse(tableConfigString) : {};
        if (tableConfig) {
            tableConfig[this.tableId] = config;
        } else {
            tableConfig = { [this.tableId]: config };
        }

        localStorage.setItem(CONFIG_KEY, JSON.stringify(tableConfig));
    }

    private generateTableId() {
        const id =
            (this.id || 'table') +
            '@' +
            this.router.routerState?.snapshot?.url.slice(0, this.router.routerState?.snapshot?.url.indexOf('?'));
        return id;
    }

    private createConfig() {
        const config: Config = {};

        this.flatColumns.forEach((col) => {
            config[col.key] = col.visible;
        });

        return config;
    }

    private updateDisplayedColumns(config: Config) {
        const newColumns = sortBy(
            this.flatColumns.filter((c) => config[c.key] === true),
            (col) => col.position
        ).map((col) => col.key);
        this.displayedColumns$.next(newColumns);
    }

    private hasCorrectKeys(config: Config) {
        // check if config contains correct keys
        const configKeys = Object.keys(config);
        const allKeys = flattenDeep(this.columnGroups.map((g) => g.columns.map((c) => c.key)));

        const filtered = allKeys?.filter((col) => configKeys.indexOf(col) === -1);
        return filtered.length === 0 && allKeys.length === configKeys.length;
    }
}
